/**
 * Security Monitor contracts — the unified shape for a security-relevant
 * event, shared between the dashboard server (which records + aggregates
 * them) and the web client (which renders the /admin/security feed).
 *
 * The dashboard records its OWN auth denials in real time (the errorHandler
 * capture point); cross-service rows (portal-api login failures, gateway
 * token rejections, crm-api access) are pulled in on the pulse timer and
 * merged into the same shape.
 */

export type SecuritySeverity = "info" | "warn" | "critical";

/** Numeric rank for severity comparisons/filters (info < warn < critical). */
export const SECURITY_SEVERITY_RANK: Record<SecuritySeverity, number> = {
  info: 0,
  warn: 1,
  critical: 2,
};

export type SecurityOutcome = "denied" | "failed" | "success";

/** All outcomes, ordered worst→benign, for filter UIs + validation. */
export const SECURITY_OUTCOMES: readonly SecurityOutcome[] = [
  "denied",
  "failed",
  "success",
] as const;

/** Where the event originated (the service surface the event occurred on).
 * For proxy-observed denials this is the upstream the dashboard was
 * calling on behalf of the actor. */
export type SecuritySource =
  | "dashboard"
  | "portal-api"
  | "ai-gateway"
  | "crm-api"
  | "agents";

/** All sources, for filter UIs + validation. */
export const SECURITY_SOURCES: readonly SecuritySource[] = [
  "dashboard",
  "portal-api",
  "ai-gateway",
  "crm-api",
  "agents",
] as const;

/**
 * Well-known `action` kinds the dashboard records for SUCCESSFUL access.
 * Denials use free-form actions (`authz.denied`, `token.invalid`, …); these
 * name the "succeeded but shouldn't have" cases the Security console flags.
 *
 *   - `elevatedAccess` — a 2xx that used a permission OUTSIDE the actor's
 *     LIVE role bundle, i.e. one an admin granted them personally in
 *     /admin/users (a per-user override). Recorded at `warn` and coalesced
 *     to one row per actor/permission/day: it is a standing, deliberate
 *     admin decision being exercised, so it belongs in the trail, but it is
 *     NOT an alarm and must not be re-stamped on every request.
 *   - `accessBypass`   — a 2xx WITHOUT the gating permission on the token at
 *     all (an upstream failed to enforce — the strongest signal). The only
 *     `critical` success, never coalesced.
 *
 * Normal sensitive reads use a dynamic `access.<source>` action; build one
 * with {@link accessAction} so the string stays consistent across server+web.
 */
export const SECURITY_ACTIONS = {
  elevatedAccess: "authz.elevated_access",
  accessBypass: "authz.bypass",
} as const;

/** The `action` for a normal (in-baseline) sensitive access on `source`. */
export function accessAction(source: SecuritySource): string {
  return `access.${source}`;
}

/** True when an event is an anomalous success — someone saw/did something a
 * blocked attempt would normally have stopped, because ENFORCEMENT FAILED
 * (`authz.bypass`: a 2xx with the gating permission nowhere on the token).
 * Drives the headline tile + alarm banner.
 *
 * Deliberately does NOT count `authz.elevated_access` (warn): an admin
 * granting someone a per-user override and that person then using it is a
 * decision the dashboard itself recorded, not an intrusion. Counting it here
 * buried real signals under thousands of rows from one correctly-provisioned
 * user (see docs/security-monitor.md → "Why elevated_access is not an
 * alarm"). */
export function isAnomalousAccess(e: {
  outcome: SecurityOutcome;
  severity: SecuritySeverity;
}): boolean {
  return e.outcome === "success" && e.severity === "critical";
}

export interface SecurityEvent {
  id: number;
  /** UTC ISO-8601 event time. */
  ts: string;
  source: SecuritySource;
  /** Upstream row id for cross-source dedupe; null for locally-recorded rows. */
  source_id: string | null;
  /** Email or app/issuer that acted; null = anonymous/unknown. */
  actor: string | null;
  /** Dotted event kind, e.g. "authz.denied", "auth.two_factor_required",
   * "token.invalid", "auth.login_failed". */
  action: string;
  severity: SecuritySeverity;
  ip: string | null;
  outcome: SecurityOutcome;
  /** Free-form structured context: { code, path, method, reason, ... }. */
  detail: Record<string, unknown> | null;
}

/** What a caller supplies to record a new event — server fills id + ts. */
export type SecurityEventInput = Omit<SecurityEvent, "id" | "ts">;

/**
 * Window-wide aggregates for the Security console. Computed over the FULL
 * window server-side (not just the returned/paginated page), so the tiles +
 * breakdown cards stay accurate even when `events` is capped by `limit`.
 */
export interface SecurityTotals {
  /** Total events in the window (ignoring the page limit). */
  total: number;
  bySeverity: Record<SecuritySeverity, number>;
  byOutcome: Record<SecurityOutcome, number>;
  /** Keyed by {@link SecuritySource}; absent sources omitted. */
  bySource: Record<string, number>;
  /** `success & critical` — the "someone saw what they shouldn't" headline. */
  anomalies: number;
}

export interface SecurityFeedResponse {
  events: SecurityEvent[];
  /** Count per action over the summary window (e.g. last 24h). */
  summary: Record<string, number>;
  /** Window-wide aggregates for the console tiles + breakdowns. */
  totals: SecurityTotals;
  generated_at: string;
}
